import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Stamp, Gift, Users, TrendingUp, ArrowRight, 
  QrCode, Megaphone, Zap, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardHome = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, activityRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/activity?limit=5`)
      ]);
      setStats(statsRes.data);
      setActivity(activityRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { 
      label: 'Sellos este mes', 
      value: stats.stamps_this_month, 
      limit: stats.plan_limit,
      icon: <Stamp className="w-6 h-6" />,
      color: 'primary'
    },
    { 
      label: 'Vouchers emitidos', 
      value: stats.total_vouchers, 
      icon: <Gift className="w-6 h-6" />,
      color: 'secondary'
    },
    { 
      label: 'Clientes únicos', 
      value: stats.unique_customers, 
      icon: <Users className="w-6 h-6" />,
      color: 'accent'
    },
    { 
      label: 'Tasa de canje', 
      value: `${stats.redemption_rate}%`, 
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'primary'
    },
  ] : [];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4 }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
          ¡Hola, {user?.business_name}! 👋
        </h1>
        <p className="text-slate-600 mt-1">
          Aquí tienes el resumen de tu programa de fidelización
        </p>
      </div>

      {/* Quick Actions */}
      {stats?.active_campaigns === 0 && (
        <motion.div 
          className="bg-gradient-to-r from-primary to-indigo-600 rounded-2xl p-6 text-white"
          {...fadeInUp}
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">¡Crea tu primera campaña!</h3>
                <p className="text-white/80">Empieza a fidelizar clientes ahora</p>
              </div>
            </div>
            <Link to="/dashboard/campaigns">
              <Button className="bg-white text-primary hover:bg-white/90">
                Crear campaña <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <motion.div
            key={i}
            className="card-stat group hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}
          >
            <div className={`absolute top-0 right-0 w-24 h-24 bg-${stat.color}/5 rounded-full -translate-y-1/2 translate-x-1/2`} />
            <div className="flex items-center justify-between relative z-10">
              <div className={`w-12 h-12 bg-${stat.color}/10 rounded-xl flex items-center justify-center text-${stat.color}`}>
                {stat.icon}
              </div>
              {stat.limit && (
                <span className="text-xs text-slate-400 font-medium">
                  /{stat.limit}
                </span>
              )}
            </div>
            <div className="mt-4 relative z-10">
              <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
              <p className="text-sm text-slate-500">{stat.label}</p>
            </div>
            {stat.limit && (
              <Progress 
                value={(stat.value / stat.limit) * 100} 
                className="mt-3 h-1.5"
              />
            )}
          </motion.div>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <motion.div 
          className="card-dashboard"
          {...fadeInUp}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">Actividad reciente</h2>
            <Link to="/dashboard/activity" className="text-primary text-sm font-medium hover:underline">
              Ver todo
            </Link>
          </div>
          
          {activity.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>Sin actividad todavía</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activity.map((event, i) => (
                <div 
                  key={i}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${event.double_stamp ? 'bg-secondary/10 text-secondary' : 'bg-primary/10 text-primary'}`}>
                    <Stamp className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      +{event.stamps_added} sello{event.stamps_added > 1 ? 's' : ''}
                      {event.double_stamp && <span className="text-secondary ml-1">(2x)</span>}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {event.phone_hash?.substring(0, 8)}...
                    </p>
                  </div>
                  <span className="text-xs text-slate-400">
                    {new Date(event.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Quick Links */}
        <motion.div 
          className="card-dashboard"
          {...fadeInUp}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-lg font-bold text-slate-900 mb-4">Acciones rápidas</h2>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/dashboard/campaigns">
              <div className="p-4 bg-slate-50 rounded-xl hover:bg-primary/5 hover:border-primary/20 border border-transparent transition-all group">
                <Megaphone className="w-8 h-8 text-primary mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-900">Campañas</p>
                <p className="text-xs text-slate-500">Crear o editar</p>
              </div>
            </Link>
            <Link to="/dashboard/devices">
              <div className="p-4 bg-slate-50 rounded-xl hover:bg-secondary/5 hover:border-secondary/20 border border-transparent transition-all group">
                <QrCode className="w-8 h-8 text-secondary mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-900">Códigos QR</p>
                <p className="text-xs text-slate-500">Generar nuevos</p>
              </div>
            </Link>
            <Link to="/dashboard/vouchers">
              <div className="p-4 bg-slate-50 rounded-xl hover:bg-accent/5 hover:border-accent/20 border border-transparent transition-all group">
                <Gift className="w-8 h-8 text-accent mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-900">Vouchers</p>
                <p className="text-xs text-slate-500">Canjear premios</p>
              </div>
            </Link>
            <Link to="/dashboard/settings">
              <div className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 border border-transparent transition-all group">
                <TrendingUp className="w-8 h-8 text-slate-600 mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-900">Mejorar plan</p>
                <p className="text-xs text-slate-500">Más sellos</p>
              </div>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardHome;
